import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { CapacityCheckResult } from '@tailoring-crm/shared';
import { Badge } from '@tailoring-crm/ui';
import { formatDateLong, formatMinutes } from '../../lib/format.js';

/**
 * The answer, rendered.
 *
 * NOTHING ON THIS SCREEN IS WORKED OUT HERE. The verdict, the earliest date that would work, the
 * bottleneck, the shortfall and every figure in the day table are fields on the response. No date
 * is computed, no day's load is summed and no feasibility is judged in the browser — if a number
 * is not on the answer, it is not shown. The only thing this file does to a number is format it.
 *
 * ACHIEVABLE AND NOT-ACHIEVABLE ARE TOLD APART BY WORDS. The badge is coloured, but the badge
 * also says which it is and the sentence above it says it again in full; a shop reading this on a
 * washed-out phone screen in daylight must never be relying on green against red.
 *
 * The earliest deliverable date appears ONLY on a no. On a yes it would be a second date beside
 * the one just approved, which is a question nobody asked.
 */

export function CapacityResult({ result }: { result: CapacityCheckResult }): ReactElement {
  const { t } = useTranslation();
  const { assumptions } = result;

  return (
    <section className="flex flex-col gap-4" aria-label={t('capacity.result.heading')}>
      <div className="flex flex-col gap-2 rounded-lg border border-border p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={result.feasible ? 'success' : 'destructive'}>
            {result.feasible ? t('capacity.result.verdictYes') : t('capacity.result.verdictNo')}
          </Badge>
          <p className="text-sm font-medium" role="status">
            {result.feasible
              ? t('capacity.result.feasible', { date: formatDateLong(result.requestedDay) })
              : t('capacity.result.notFeasible', { date: formatDateLong(result.requestedDay) })}
          </p>
        </div>

        {!result.feasible ? (
          <dl className="flex flex-col gap-1 text-sm">
            <div className="flex flex-wrap justify-between gap-2">
              <dt className="text-muted-foreground">{t('capacity.result.earliest')}</dt>
              <dd className="font-medium">
                {result.earliestDeliveryDay === null
                  ? t('capacity.result.noEarliest', { count: assumptions.horizonDays })
                  : formatDateLong(result.earliestDeliveryDay)}
              </dd>
            </div>
            {result.bottleneck ? (
              <div className="flex flex-wrap justify-between gap-2">
                <dt className="text-muted-foreground">{t('capacity.result.bottleneck')}</dt>
                <dd className="font-medium">{result.bottleneck.label}</dd>
              </div>
            ) : null}
            <div className="flex flex-wrap justify-between gap-2">
              <dt className="text-muted-foreground">{t('capacity.result.shortfall')}</dt>
              <dd className="font-medium tabular-nums">{formatMinutes(result.shortfallMinutes)}</dd>
            </div>
          </dl>
        ) : null}

        {assumptions.unestimatedAssignmentCount > 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('capacity.result.assumedEstimates', {
              count: assumptions.unestimatedAssignmentCount,
              duration: formatMinutes(assumptions.unestimatedAssignmentMinutes),
            })}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">{t('capacity.result.days')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th scope="col" className="py-1 font-medium">
                  {t('capacity.result.day')}
                </th>
                <th scope="col" className="py-1 text-right font-medium">
                  {t('capacity.result.capacity')}
                </th>
                <th scope="col" className="py-1 text-right font-medium">
                  {t('capacity.result.committed')}
                </th>
                <th scope="col" className="py-1 text-right font-medium">
                  {t('capacity.result.proposed')}
                </th>
                <th scope="col" className="py-1 text-right font-medium">
                  {t('capacity.result.free')}
                </th>
                <th scope="col" className="py-1 text-right font-medium">
                  {t('capacity.result.status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {result.days.map((day) => (
                <tr key={day.day} data-testid="capacity-day" className="border-t border-border">
                  <th scope="row" className="py-1.5 text-left font-normal">
                    {formatDateLong(day.day)}
                  </th>
                  <td className="py-1.5 text-right tabular-nums">
                    {formatMinutes(day.capacityMinutes)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {formatMinutes(day.committedMinutes)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {formatMinutes(day.proposedMinutes)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {formatMinutes(day.freeMinutes)}
                  </td>
                  <td className="py-1.5 text-right">
                    {!day.workingDay ? (
                      <span className="text-muted-foreground">{t('capacity.result.closed')}</span>
                    ) : day.overtimeMinutes > 0 ? (
                      <Badge variant="warning">{t('capacity.result.overCapacity')}</Badge>
                    ) : (
                      <span className="text-muted-foreground">
                        {t('capacity.result.withinCapacity')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
